/**
 * What the module knows about the application it is hanging inside.
 *
 * Three things it has to be told: how to call the API, which screen the reader
 * is on, and which build is running. Nothing else has to be told, because the
 * moment this module knows a fourth thing about its host it stops being
 * copyable into the next one (FR-23).
 *
 * `rasterise` is a fourth and it is not one of those: it has a working default
 * and a host never passes it. It is a seam so that the picture of the page can
 * be exercised without a browser, which is what `screenshot.ts` was written
 * for and what nothing used until the panel landed beside it. A host may still
 * pass one, and the case that would want to is an application whose pages
 * `html2canvas` draws wrongly.
 *
 * The QueryClientProvider is the module's own. TanStack Query is what the
 * coding standards name for server state (11.10), and a nested provider gives
 * this subtree its own cache without requiring the host to have set one up.
 * That keeps the adoption budget at one mount point (FR-23a): a host that
 * already uses Query is not disturbed, and a host that does not is not forced
 * into it.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createContext, useContext, useMemo, useRef, type ReactNode } from "react";

import html2canvas from "html2canvas";

import { FeedbackClient } from "./client";
import type { Rasteriser } from "./screenshot";

export interface FeedbackContextValue {
  client: FeedbackClient;
  /** The path the reader is on. Only the client knows it, so the client claims it. */
  currentScreen: string;
  /** The commit the bundle was built from. Same. */
  build: string;
  /** What paints the page for a screenshot (FR-21). `html2canvas` unless a host says otherwise. */
  rasterise: Rasteriser;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

export interface FeedbackProviderProps {
  apiBase: string;
  getToken: () => Promise<string> | string;
  currentScreen: string;
  build: string;
  /** Optional, and a host has no reason to pass it. See the module docstring. */
  rasterise?: Rasteriser;
  children: ReactNode;
}

export function FeedbackProvider({
  apiBase,
  getToken,
  currentScreen,
  build,
  rasterise = html2canvas,
  children,
}: FeedbackProviderProps) {
  // getToken is deliberately not a dependency. A host is free to rebuild that
  // function on every render, and rebuilding the client on it would throw the
  // cache away each time. The address is what identifies a client, and the
  // token is asked for per call anyway, so a fresh one always reaches us.
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const client = useMemo(
    () => new FeedbackClient({ apiBase, getToken: () => tokenRef.current() }),
    [apiBase],
  );
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // A submission list is read by somebody who has just sent
            // something, so a long stale window is wrong here even though it
            // is right almost everywhere else.
            staleTime: 5_000,
            refetchOnWindowFocus: false,
            // No retry. The non-functional requirements say this surface fails
            // visibly rather than quietly: a reporter who is told at once that
            // it did not send still has his text and can press the button
            // again, and three silent attempts only make that take longer.
            retry: false,
          },
        },
      }),
    [],
  );
  const value = useMemo(
    () => ({ client, currentScreen, build, rasterise }),
    [client, currentScreen, build, rasterise],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>
    </QueryClientProvider>
  );
}

export function useFeedback(): FeedbackContextValue {
  const value = useContext(FeedbackContext);
  if (!value) {
    throw new Error(
      "A feedback surface was rendered outside FeedbackProvider. Mount FeedbackLauncher " +
        "once in the application shell, and wrap the feedback screen in the same provider.",
    );
  }
  return value;
}
